import { useEffect, useMemo, useState } from "react";
import { useInRouterContext, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ThemeClasses } from "../../../types/components";
import * as activitiesService from "../services/activitiesService";
import type { WorkProjectOption } from "../services/activitiesService";
import { EMPTY_SCOPE, hasScope, scopeOptions, type ActivityScope, type ScopeRow } from "../lib/activityScope";

// ---------------------------------------------------------------------------
// Filtros de alcance (cliente · proyecto · repositorio) de Tablero, Backlog y
// Roadmap. El valor vive en la URL (`?client=&project=&repo=`) para que un enlace
// compartido abra la misma vista; el catálogo de proyectos de Horas se pide una
// vez por sesión. El interruptor «Issues de GitHub» sigue en `useShowGithub`.
// ---------------------------------------------------------------------------

let projectsCache: Promise<WorkProjectOption[]> | null = null;
const loadProjects = () => {
  if (!projectsCache) {
    // El servicio se importa como módulo para que las pruebas que lo simulan
    // sin esta función no se caigan: sin proyectos los filtros solo no se pintan.
    let fetch: (() => Promise<WorkProjectOption[]>) | undefined;
    try {
      fetch = activitiesService.fetchWorkProjects;
    } catch {
      fetch = undefined; // un simulacro estricto lanza al leer un export que no definió
    }
    projectsCache = (typeof fetch === "function" ? fetch() : Promise.resolve([] as WorkProjectOption[])).catch((err) => {
      projectsCache = null;
      throw err;
    });
  }
  return projectsCache;
};

/** Alcance leído de la URL + catálogo de proyectos (para filtrar y para las opciones). */
export function useActivityScope(): { scope: ActivityScope; setScope: (patch: Partial<ActivityScope>) => void; projects: WorkProjectOption[] } {
  // Fuera de un Router (pruebas, incrustaciones) el alcance vive en memoria.
  const inRouter = useInRouterContext();
  const [localParams, setLocalParams] = useState(() => new URLSearchParams());
  const routerPair = inRouter ? useSearchParams() : null; // eslint-disable-line react-hooks/rules-of-hooks -- `inRouter` no cambia en vida del componente
  const params = routerPair ? routerPair[0] : localParams;
  const setParams = (next: URLSearchParams, opts?: { replace?: boolean }) => (routerPair ? routerPair[1](next, opts) : setLocalParams(next));
  const [projects, setProjects] = useState<WorkProjectOption[]>([]);
  useEffect(() => {
    let alive = true;
    loadProjects()
      .then((list) => alive && setProjects(list))
      .catch(() => alive && setProjects([]));
    return () => {
      alive = false;
    };
  }, []);
  const scope = useMemo<ActivityScope>(
    () => ({ client: params.get("client") ?? "", project: params.get("project") ?? "", repo: params.get("repo") ?? "" }),
    [params],
  );
  const setScope = (patch: Partial<ActivityScope>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries({ ...scope, ...patch })) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };
  return { scope, setScope, projects };
}

interface Props {
  scope: ActivityScope;
  setScope: (patch: Partial<ActivityScope>) => void;
  projects: WorkProjectOption[];
  /** Filas visibles antes del alcance: de ellas salen los repos que se ofrecen. */
  rows: readonly ScopeRow[];
  tc: ThemeClasses;
  /** `sm` para la barra compacta del Tablero. */
  size?: "sm" | "md";
}

export default function ActivityScopeFilters({ scope, setScope, projects, rows, tc, size = "md" }: Props) {
  const { t } = useTranslation("common");
  const options = useMemo(() => scopeOptions(rows, projects), [rows, projects]);
  const cls = size === "sm" ? `rounded-lg border px-2 py-1.5 text-xs ${tc.border} ${tc.cardBg} ${tc.text}` : `rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`;
  const all = t("staffActivities.scope.all");
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="activity-scope">
      {options.clients.length > 0 && (
        <select value={scope.client} onChange={(e) => setScope({ client: e.target.value })} className={cls} aria-label={t("staffActivities.scope.client")} data-testid="scope-client">
          <option value="">{t("staffActivities.scope.client")}: {all}</option>
          {options.clients.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      )}
      {options.projects.length > 0 && (
        <select value={scope.project} onChange={(e) => setScope({ project: e.target.value })} className={cls} aria-label={t("staffActivities.scope.project")} data-testid="scope-project">
          <option value="">{t("staffActivities.scope.project")}: {all}</option>
          {options.projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      )}
      {options.repos.length > 0 && (
        <select value={scope.repo} onChange={(e) => setScope({ repo: e.target.value })} className={cls} aria-label={t("staffActivities.scope.repo")} data-testid="scope-repo">
          <option value="">{t("staffActivities.scope.repo")}: {all}</option>
          {options.repos.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      )}
      {hasScope(scope) && (
        <button type="button" onClick={() => setScope(EMPTY_SCOPE)} className={`${cls} ${tc.buttonHover}`} data-testid="scope-clear">
          <i className="ri-close-line" /> {t("staffActivities.scope.clear")}
        </button>
      )}
    </div>
  );
}
